// Nutrition analytics — config + extractor. Inherits the shared engine/dashboard.
//
// Metrics are daily TOTALS (sum) of the macros. A meals-logged streak lives on
// the kcal metric (streak:true) — Nutrition has no existing streak display, so
// this ADDS one via the dashboard rather than duplicating.
//
// SCOPE NOTE: hydration is intentionally excluded — useNutrition exposes only
// TODAY's cups (no history array), so a hydration trend would need a new fetch
// (a hook change), which is out of "config + wiring" scope. Flagged as a
// follow-up in the PR.
//
// GUARDRAIL (Nutrition = no prescription): declares only totals/trend. The
// dashboard renders ▲/▼ + a neutral projection — no "eat more protein" copy.

import type { Sample } from "./engine";
import type { DashboardConfig } from "./dashboard";
import type { NutritionLog } from "@/hooks/useNutrition";

export const NUTRITION_DASHBOARD: DashboardConfig = {
  moduleKey: "nutrition",
  titleKey: "nutrition.analytics.title",
  defaultGranularity: "week",
  metrics: [
    {
      key: "kcal",
      labelKey: "nutrition.analytics.metric.kcal",
      reducer: "sum",
      color: "hsl(30 90% 55%)",
      unitKey: "nutrition.analytics.unit.kcal",
      chart: "line",
      projectable: true,
      streak: true, // days with any logged meal
    },
    {
      key: "protein",
      labelKey: "nutrition.analytics.metric.protein",
      reducer: "sum",
      color: "hsl(187 72% 55%)",
      unitKey: "nutrition.analytics.unit.g",
      chart: "line",
      projectable: true,
    },
    {
      key: "carbs",
      labelKey: "nutrition.analytics.metric.carbs",
      reducer: "sum",
      color: "hsl(265 60% 65%)",
      unitKey: "nutrition.analytics.unit.g",
      chart: "line",
      projectable: false,
    },
    {
      key: "fat",
      labelKey: "nutrition.analytics.metric.fat",
      reducer: "sum",
      color: "hsl(48 90% 55%)",
      unitKey: "nutrition.analytics.unit.g",
      chart: "line",
      projectable: false,
    },
  ],
};

// `date` is a DATE-only column; parse at LOCAL noon so day bucketing never
// drifts across the UTC boundary.
function logDayMs(date: string): number {
  return new Date(date + "T12:00:00").getTime();
}

export function extractNutritionSamples(rows: NutritionLog[]): Record<string, Sample[]> {
  const kcal: Sample[] = [];
  const protein: Sample[] = [];
  const carbs: Sample[] = [];
  const fat: Sample[] = [];

  for (const r of rows) {
    if (!r.date) continue;
    const t = logDayMs(r.date);
    if (!Number.isFinite(t)) continue;
    const category = r.meal_type;

    if (typeof r.kcal === "number") kcal.push({ t, v: r.kcal, category });
    if (typeof r.protein === "number") protein.push({ t, v: r.protein, category });
    if (typeof r.carbs === "number") carbs.push({ t, v: r.carbs, category });
    if (typeof r.fat === "number") fat.push({ t, v: r.fat, category });
  }

  return { kcal, protein, carbs, fat };
}
